import React from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import SEO from "../../hooks/SEO";
import HeroSection from "../../components/HeroSection";
import StatsStrip from "../../components/StatsStrip";
import SectionHeader from "../../components/SectionHeader";
import ServiceCard from "../../components/ServiceCard";
import CapabilityCard from "../../components/CapabilityCard";
import PortfolioCompanyCard from "../../components/PortfolioCompanyCard";
import CTASection from "../../components/CTASection";
import BlogCard from "../../components/BlogCard";
import {
  aboutHighlights,
  companyNarrative,
  mediaCards,
  partnerLogos,
  portfolioCompanies,
  posts,
  projectSupportAreas,
  seoContent,
  serviceCards,
  siteMeta,
  stats,
  supportCapabilities,
} from "../../data/mockData";

const Landing = () => (
  <>
    <SEO
      title={seoContent.home.title}
      description={seoContent.home.description}
      name={siteMeta.name}
      type="website"
    />

    <HeroSection siteMeta={siteMeta} services={serviceCards} />
    <StatsStrip items={stats} />

    <Box className="site-section">
      <Container>
        <Grid
          container
          spacing={{ xs: 4, md: 4.5, lg: 5 }}
          alignItems="stretch"
          sx={{ width: "100%", m: 0 }}
        >
          <Grid item xs={12} lg={7}>
            <SectionHeader
              eyebrow="About Refwa"
              title="Established Operations"
              description={companyNarrative.overview}
            />
            <Stack spacing={2.2} sx={{ maxWidth: 700 }}>
              {companyNarrative.body.slice(0, 2).map((paragraph) => (
                <Typography
                  key={paragraph}
                  variant="body1"
                  color="text.secondary"
                >
                  {paragraph}
                </Typography>
              ))}
            </Stack>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.8}
              sx={{ mt: 3.5 }}
            >
              <Button
                component={RouterLink}
                to="/about"
                variant="contained"
                size="large"
              >
                Company Profile
              </Button>
              <Button
                component={RouterLink}
                to="/services"
                variant="outlined"
                size="large"
              >
                Explore core services
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={12} lg={5}>
            <Paper
              sx={{
                height: "100%",
                p: { xs: 3, md: 3.6 },
                border: "1px solid var(--color-border)",
                boxShadow: "var(--shadow-card)",
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(252,248,242,0.96))",
              }}
            >
              <Box className="eyebrow">Corporate footprint</Box>
              <Typography variant="h4" sx={{ mt: 1.5, maxWidth: 380 }}>
                Regional Reach
              </Typography>
              <Stack
                direction="row"
                spacing={1.1}
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 2.6 }}
              >
                {siteMeta.regions.map((region) => (
                  <Typography
                    key={region}
                    variant="body2"
                    sx={{
                      px: 1.5,
                      py: 0.9,
                      borderRadius: 999,
                      border: "1px solid var(--color-border)",
                      bgcolor: "#fffdf9",
                      fontWeight: 700,
                      color: "text.secondary",
                    }}
                  >
                    {region}
                  </Typography>
                ))}
              </Stack>
              <Stack spacing={1.4} sx={{ mt: 3.2 }}>
                {aboutHighlights.map((item) => (
                  <Box
                    key={item.title}
                    sx={{
                      p: 2,
                      borderRadius: 0.5,
                      border: "1px solid var(--color-border)",
                      bgcolor: "rgba(244,238,228,0.56)",
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.7 }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>

    <Box className="site-section site-section--white">
      <Container>
        <SectionHeader
          eyebrow="Core Services"
          title="Core Services"
          description="Mining, petroleum services, and international trade remain central to Refwa's operations, alongside construction, equipment, environmental, and sustainability-related services."
        />
        <Grid container spacing={3} sx={{ width: "100%", m: 0 }}>
          {serviceCards.map((service) => (
            <Grid item xs={12} md={6} lg={4} key={service.id}>
              <ServiceCard {...service} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>

    <Box className="site-section site-section--muted">
      <Container>
        <SectionHeader
          eyebrow="Featured Sectors"
          title="Key Sectors"
          description="Refwa's sector activity spans mining, petroleum services, and international trade, supported by practical operating capability."
        />
        <Grid container spacing={3} sx={{ width: "100%", m: 0 }}>
          {mediaCards.map((item) => (
            <Grid item xs={12} md={6} lg={4} key={item.id}>
              <Paper
                sx={{
                  overflow: "hidden",
                  border: "1px solid var(--color-border)",
                  boxShadow: "var(--shadow-card)",
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(252,248,242,0.96))",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    aspectRatio: "16 / 10",
                    bgcolor: "#122033",
                  }}
                >
                  <Box
                    component="img"
                    src={item.image}
                    alt={item.title}
                    loading="lazy"
                    decoding="async"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      opacity: 0.82,
                    }}
                  />
                </Box>
                <Box sx={{ p: { xs: 2.75, md: 3.1 } }}>
                  <Typography variant="h5">{item.title}</Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1.5, lineHeight: 1.8 }}
                  >
                    {item.description}
                  </Typography>
                  <Typography
                    component={RouterLink}
                    to={item.to}
                    variant="subtitle2"
                    sx={{
                      display: "inline-block",
                      mt: 2.25,
                      color: "secondary.main",
                      fontWeight: 800,
                    }}
                  >
                    {item.linkLabel}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>

    <Box className="site-section">
      <Container>
        <SectionHeader
          eyebrow="Portfolio Capabilities"
          title="Portfolio Companies"
          description="Refwa's wider operating interests extend across construction, equipment support, environmental services, facilities, and water-related activity."
        />
        <Grid container spacing={3} sx={{ width: "100%", m: 0 }}>
          {portfolioCompanies.map((company) => (
            <Grid item xs={12} md={6} lg={4} key={company.id}>
              <PortfolioCompanyCard {...company} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>

    <Box className="site-section site-section--white">
      <Container>
        <SectionHeader
          eyebrow="Project Support"
          title="Project Support"
          description="Refwa supports projects through site preparation, civil works, equipment services, operational support, and sustainability-related activity."
        />
        <Grid container spacing={3} sx={{ width: "100%", m: 0 }}>
          {supportCapabilities.map((item) => (
            <Grid item xs={12} md={6} lg={4} key={item.title}>
              <CapabilityCard {...item} />
            </Grid>
          ))}
        </Grid>
        <Box
          sx={{
            mt: 4,
            p: { xs: 2.4, md: 2.9 },
            borderRadius: "var(--radius-card)",
            border: "1px solid var(--color-border)",
            background:
              "linear-gradient(180deg, rgba(244,238,228,0.82), rgba(255,255,255,0.92))",
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
            sx={{ color: "text.secondary" }}
          >
            {projectSupportAreas.map((item) => (
              <Typography
                key={item}
                variant="body2"
                sx={{
                  px: 1.9,
                  py: 1.1,
                  borderRadius: 10,
                  border: "1px solid var(--color-border)",
                  bgcolor: "#fffdf9",
                }}
              >
                {item}
              </Typography>
            ))}
          </Stack>
        </Box>
      </Container>
    </Box>

    <Box className="site-section site-section--dark">
      <Container>
        <SectionHeader
          eyebrow="Partners and Affiliations"
          title="Partners"
          description="Selected partner and affiliation references associated with Refwa."
          align="center"
        />
        <Grid
          container
          spacing={3}
          justifyContent="center"
          sx={{ width: "100%", m: 0 }}
        >
          {partnerLogos.map((partner) => {
            const content = (
              <Paper
                sx={{
                  minHeight: 128,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: { xs: 2.25, md: 2.75 },
                  backgroundColor: "#fff",
                  border: "1px solid rgba(19,33,51,0.08)",
                  boxShadow: "var(--shadow-card)",
                }}
              >
                <Box
                  component="img"
                  src={partner.logo}
                  alt={partner.name}
                  loading="lazy"
                  decoding="async"
                  sx={{ maxHeight: 72, width: "auto", objectFit: "contain" }}
                />
              </Paper>
            );

            return (
              <Grid item xs={6} sm={4} md={3} lg={2} key={partner.id}>
                {partner.url ? (
                  <Box
                    component="a"
                    href={partner.url}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ display: "block" }}
                  >
                    {content}
                  </Box>
                ) : (
                  content
                )}
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>

    <Box className="site-section site-section--white">
      <Container>
        <SectionHeader
          eyebrow="Articles"
          title="Articles"
          description="Commentary on mining, trade, petroleum services, industrial delivery, and project support."
        />
        <Grid container spacing={3} sx={{ width: "100%", m: 0 }}>
          {posts.map((post) => (
            <Grid item xs={12} md={6} lg={4} key={post.id}>
              <BlogCard
                title={post.title}
                summary={post.summary}
                category={post.category}
                cover={post.cover}
                to={`/articles/${post.slug}`}
              />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>

    <CTASection
      title="Discuss a Requirement"
      description="For formal inquiries, please use Refwa's listed contact details."
      secondaryLabel="Read Articles"
      secondaryTo="/articles"
    />
  </>
);

export default Landing;
